// Text resolution and wrapping shared by canvas, preflight, export, layout gates
// and the API. A single implementation keeps every consumer on the same lines.
function resolveText(node, content) {
  if (node.textOverride !== undefined) return node.textOverride;
  if (!node.binding) return node.text;
  if (node.preserveOriginal) {
    if (node.role === "price") {
      const raw = content[node.binding.replace(":price", ":rawPrices")];
      if (raw === undefined || raw === node.sourceSnapshot)
        return node.originalText;
      const prices = JSON.parse(raw || "[]");
      if (!prices.length) return "market price";
      const sizes = JSON.parse(
        content[node.binding.replace(":price", ":sizes")] || "[]"
      );
      return prices
        .map(
          (p, i) =>
            `${
              node.originalText
                .toLowerCase()
                .includes(String(sizes[i]).toLowerCase())
                ? `${sizes[i]} `
                : ""
            }${Number(p).toFixed(2)}`
        )
        .join(" • ");
    }
    if (
      content[node.binding] === undefined ||
      content[node.binding] === node.sourceSnapshot
    )
      return node.originalText;
  }
  if (node.role === "price" && node.priceFormat === "bullet")
    return (content[node.binding] ?? node.text).replace(/ \/ /g, " • ");
  return content[node.binding] ?? node.text;
}

function transformText(text, style = {}) {
  const value = String(text ?? "");
  return style.textTransform === "uppercase" ? value.toUpperCase()
    : style.textTransform === "lowercase" ? value.toLowerCase()
    : style.textTransform === "titlecase" ? value.toLocaleLowerCase().replace(/\p{L}+/gu, (word, offset) => {
      if (word === "ipa") return "IPA";
      const minor = /^(and|or|with|of|the|a|an|in|on|to|for|from|by)$/.test(word);
      const earlier = value.slice(0, offset);
      return minor && earlier.trim() && !/[—:–]\s*$/.test(earlier)
        ? word : word.charAt(0).toLocaleUpperCase() + word.slice(1);
    }) : value;
}

function wrapText(text, style, width, measure) {
  const max = Math.max(8, width);
  const getWidth =
    measure ||
    ((str) =>
      str.length * style.fontSize * 0.53 +
      Math.max(0, str.length - 1) * (style.letterSpacing || 0));
  const lines = [];
  transformText(text, style)
    .split("\n")
    .forEach((paragraph) => {
      let line = "";
      paragraph
        .split(/\s+/)
        .filter(Boolean)
        .forEach((word) => {
          const candidate = line ? `${line} ${word}` : word;
          if (getWidth(candidate) <= max) {
            line = candidate;
            return;
          }
          if (line) {
            lines.push(line);
            line = "";
          }
          // Monetary amounts are indivisible, even when their frame is too narrow.
          if (/^[£€$]?\d+(?:[.,]\d+)*$/.test(word)) { line = word; return; }
          for (const char of word) {
            if (line && getWidth(line + char) > max) {
              lines.push(line);
              line = "";
            }
            line += char;
          }
        });
      lines.push(line);
    });
  return lines;
}

// Deterministic width estimate used when no font measurer is available
// (server, tests). Browsers inject a canvas measurer via setMeasurer.
function estimateWidth(text, style) {
  const value = String(text);
  return value.length * style.fontSize * 0.53 + Math.max(0, value.length - 1) * (style.letterSpacing || 0);
}
let measurer = null;
function setMeasurer(fn) { measurer = typeof fn === "function" ? fn : null; }
// Width of already-transformed text, including tracking.
function measureWidth(text, style) {
  return measurer ? measurer(String(text), style) : estimateWidth(text, style);
}
function textWidth(text, style) {
  return measureWidth(transformText(text, style), style);
}
function textLines(text, style, width) {
  return wrapText(text, style, width, (value) => measureWidth(value, style));
}
// Baseline offset of each line within a text frame, matching the canvas.
function lineBaselines(lines, style) {
  return lines.map((_, i) => style.fontSize * 0.86 + i * style.fontSize * style.lineHeight);
}
// Exactly what the canvas and every export draw for a text node: either the
// preserved imported runs (while text, frame and type are unchanged) or
// freshly wrapped lines. Positions are relative to the node frame.
function originalLayoutApplies(node, style, text) {
  const original = node.originalLayout;
  return !!original && text === original.text && node.w === original.w && node.h === original.h &&
    Object.entries(original.style || {}).every(([key, value]) => style[key] === value);
}
function textRuns(node, style, text) {
  if (originalLayoutApplies(node, style, text))
    return { original: true, runs: node.originalLayout.runs.map((r) => ({ text: r.text, x: r.x, y: r.y })) };
  const lines = textLines(text, style, node.w);
  const x = style.align === "center" ? node.w / 2 : style.align === "right" ? node.w : 0;
  const baselines = lineBaselines(lines, style);
  return { original: false, runs: lines.map((line, i) => ({ text: line, x, y: baselines[i] })) };
}

module.exports = { textRuns, originalLayoutApplies, resolveText, transformText, wrapText, textWidth, textLines, measureWidth, setMeasurer, lineBaselines, estimateWidth };
